import { convertSubunitIntoUnit } from "@oysterhr/common"
import { booleanToTextValue } from "~/app/utils/helpers"
import { LiteralUnion } from "react-hook-form"
import { VariableCompensationSummaryProps } from "~/shared/components/VariableCompensationSummary"
import {
  MoneyInput,
  VariableCompensationTypeEnum,
  VariableCompensation,
  Engagement,
  Employment,
} from "@oysterhr/schema"
import { VariableCompensationEngagementInputType } from "~/shared/components/FormSections/CompensationSection/VariableCompensation/types"
import { DateTime } from "luxon"
import type { TFunction } from "react-i18next"

type nameType = Lowercase<VariableCompensationTypeEnum>
type NameIdentifier = `${Lowercase<VariableCompensationTypeEnum>}.${string}`

export const getCompensationTypeLower = (compensationType: VariableCompensationTypeEnum) => {
  return compensationType.toLowerCase() as nameType
}

export const buildName = (compensationType: VariableCompensationTypeEnum, id: string) => {
  return `${getCompensationTypeLower(compensationType)}.${id}` as NameIdentifier
}

export const newEntryId = "newEntry" as const
export type VarCompEditingType = LiteralUnion<"newEntry", string> | null

const emptyVariableCompensation: VariableCompensationEngagementInputType = {
  amount: "",
  paymentFrequency: "",
  label: "",
  amountType: "amount",
  selectedMonths: [],
  guaranteed: "",
  prorated: undefined,
  oneTimePaidInFirstMonth: true,
}
export const convertVariableCompensationIntoInitialValue = (
  entry?: VariableCompensation,
  employmentStartDate?: string,
): VariableCompensationEngagementInputType => {
  if (!entry) return { ...emptyVariableCompensation }

  return {
    apiId: entry.id,
    label: entry.label,
    amount: entry.amount
      ? convertSubunitIntoUnit(entry.amount.subunit, entry.amount.currencyCode).toString()
      : "",
    amountAsPercentage: entry.amountAsPercentage ? entry.amountAsPercentage.toString() : undefined,
    paymentFrequency: entry.paymentFrequency,
    customLabel: entry?.customLabel ?? undefined,
    amountType: entry.amountAsPercentage != null ? "percentage" : "amount",
    selectedMonths: entry.selectedMonths ?? [],
    guaranteed: entry.guaranteed != null ? booleanToTextValue(entry.guaranteed) : undefined,
    prorated: entry.prorated !== null ? booleanToTextValue(entry.prorated) : undefined,
    effectiveDate: entry.effectiveDate,
    oneTimePaidInFirstMonth: isPaidInFirstMonth({
      startDate: employmentStartDate,
      effectiveDate: entry.effectiveDate,
    }),
    // Staff view only:
    includedInGrossSalary:
      entry.includedInGrossSalary !== null ? booleanToTextValue(entry.includedInGrossSalary) : null,
    statutory: entry.statutory != null ? booleanToTextValue(entry.statutory) : null,
    changeReason: "",
  }
}

/** Tells if start date and effective date has the same months and years. */
export const isPaidInFirstMonth = ({
  startDate,
  effectiveDate,
}: {
  startDate: Engagement["startDate"]
  effectiveDate: VariableCompensation["effectiveDate"]
}) =>
  effectiveDate
    ? DateTime.fromISO(startDate)
        .set({ day: 1 })
        .equals(DateTime.fromISO(effectiveDate).set({ day: 1 }))
    : false

export const getOneTimePaymentMonthSummary = (
  effectiveDate: VariableCompensation["effectiveDate"],
  startDate: Engagement["startDate"],
  t: TFunction,
) =>
  !startDate || !effectiveDate || isPaidInFirstMonth({ startDate, effectiveDate })
    ? t("GENERIC.summary.paidInTheFirstMonth", { ns: "variable_compensation/Common" })
    : t("GENERIC.summary.paidInScheduledMonth", {
        ns: "variable_compensation/Common",
        date: DateTime.fromISO(effectiveDate).toLocaleString({
          ...DateTime.DATE_FULL,
          day: undefined,
        }),
      })

type VariableComponensationSummaryFields = Pick<
  VariableCompensation,
  | "id"
  | "paymentFrequency"
  | "selectedMonths"
  | "amount"
  | "amountAsPercentage"
  | "prorated"
  | "compensationType"
  | "variableCompensationTemplate"
  | "editedAfterCreationFromTemplate"
  | "effectiveDate"
  | "statutory"
  | "includedInGrossSalary"
  | "guaranteed"
>
export const getSummary = (
  entry: VariableComponensationSummaryFields,
  salary?: MoneyInput,
  amountType?: "percentage" | "amount",
  hideProrated?: boolean,
  startDate?: Employment["startDate"],
) => {
  const displayProratedSummary = hideProrated
    ? false
    : entry.compensationType === VariableCompensationTypeEnum.Bonus &&
      typeof entry.prorated === "boolean"

  const baseSummary = {
    frequency: entry.paymentFrequency,
    selectedMonths: entry.selectedMonths,
    displayProratedSummary,
    prorated: entry.prorated,
    effectiveDate: entry.effectiveDate,
    startDate,
    statutory: entry.statutory,
    includedInGrossSalary: entry.includedInGrossSalary,
    guaranteed: entry.guaranteed,
    compensationType: entry.compensationType,
  }

  if (salary && (entry.amountAsPercentage || amountType === "percentage")) {
    const percentageSummary: VariableCompensationSummaryProps = {
      ...baseSummary,
      type: "percentage",
      percentage: entry.amountAsPercentage || 0,
      salary,
    }
    return percentageSummary
  } else {
    const amountSummary: VariableCompensationSummaryProps = {
      ...baseSummary,
      type: "amount",
      frequency: entry.paymentFrequency,
      amount: {
        currencyCode: entry.amount?.currencyCode || salary?.currencyCode || "USD",
        subunit: entry.amount?.subunit || 0,
      },
      templateAmount:
        !entry?.editedAfterCreationFromTemplate && entry.variableCompensationTemplate?.amount
          ? {
              currencyCode:
                entry.variableCompensationTemplate?.amount?.currencyCode ||
                salary?.currencyCode ||
                "USD",
              subunit: entry.variableCompensationTemplate?.amount?.subunit || 0,
            }
          : undefined,
    }
    return amountSummary
  }
}
